<template>
    <div class="api-key-created-content">
        <div v-if="!isMcp" class="warning-banner">
            <i class="pi pi-exclamation-triangle"></i>
            Save this key now! You won't be able to see it again.
        </div>
        <div v-if="isMcp" class="warning-banner">
            <i class="pi pi-exclamation-triangle"></i>
            Save this configuration now! You won't be able to see it again.
        </div>

        <div v-if="!isMcp" class="api-key-display">
            <span class="api-key-value">{{ apiKeyData?.key }}</span>
            <Button
                icon="pi pi-copy"
                class="p-button-rounded p-button-text"
                @click="copyApiKey(apiKeyData?.key || '')"
                :disabled="apiKeyCopied"
                :label="apiKeyCopied ? 'Copied!' : 'Copy'"
                aria-label="Copy API key to clipboard"
            />
        </div>

        <div class="api-key-info">
            <div class="info-row" v-if="!isMcp">
                <span class="info-label">Name:</span>
                {{ apiKeyData?.name }}
            </div>
            <div class="info-row" v-if="!isMcp">
                <span class="info-label">Created:</span>
                {{ formatDate(apiKeyData?.created_at) }}
            </div>
            <div class="info-row" v-if="!isMcp">
                <span class="info-label">Expires:</span>
                {{
                    apiKeyData?.expires_at
                        ? formatDate(apiKeyData?.expires_at)
                        : "Never"
                }}
            </div>
            <div class="info-row" v-if="isMcp">
                <span class="info-label">Organization:</span>
                {{ mcpData?.organization?.name }}
            </div>
            <div class="info-row" v-if="isMcp">
                <span class="info-label">Stek:</span>
                {{ mcpData?.stek?.name }}
            </div>
        </div>

        <div v-if="isMcp" class="mt-4">
            <h4>Configuration</h4>
            <div class="config-display">
                <pre class="config-value">{{ mcpConfigJson }}</pre>
                <Button
                    icon="pi pi-copy"
                    class="p-button-rounded p-button-text"
                    @click="copyMcpConfig(mcpConfigJson)"
                    :disabled="mcpConfigCopied"
                    :label="mcpConfigCopied ? 'Copied!' : 'Copy'"
                    aria-label="Copy MCP configuration to clipboard"
                />
            </div>

            <div class="mt-4">
                <h4>Claude Code</h4>
                <small class="form-help-text mb-2">
                    You can add this configuration directly to Claude Code with
                    this CLI command.
                </small>
                <div class="config-display">
                    <pre class="config-value">{{ claudeCliCommand }}</pre>
                    <Button
                        icon="pi pi-copy"
                        class="p-button-rounded p-button-text"
                        @click="copyCliCommand(claudeCliCommand)"
                        :disabled="cliCommandCopied"
                        :label="cliCommandCopied ? 'Copied!' : 'Copy'"
                        aria-label="Copy Claude CLI command to clipboard"
                    />
                </div>
            </div>
        </div>
    </div>
    <div class="buttons">
        <Button
            :label="
                isMcp
                    ? 'I\'ve Saved My Configuration'
                    : 'I\'ve Saved My API Key'
            "
            icon="pi pi-check"
            @click="dialogRef?.close"
            class="p-button-primary"
            autofocus
        />
    </div>
</template>

<script setup lang="ts">
import { ref, inject, Ref, computed, onMounted } from "vue";
import Button from "primevue/button";
import type { DynamicDialogInstance } from "primevue/dynamicdialogoptions";
import type { ApiKeyCreated } from "@/lib/openapi/gen/types.gen";
import type { Organization, Stek } from "@/datamodel";

interface McpData {
    organization?: Organization;
    stek?: Stek;
}

interface DialogData {
    apiKey?: ApiKeyCreated;
    mcpData?: McpData;
    isMcp?: boolean;
    mcpApiKey?: ApiKeyCreated;
}

/**
 * Generate MCP configuration JSON
 *
 * @param apiKey - The API key to use for authentication
 * @param organizationId - The organization ID to use in the configuration
 * @param stekId - The stek ID to use in the configuration
 * @param organizationName - The organization name to use in the server name
 * @param stekName - The stek name to use in the server name
 * @returns A JSON string containing the MCP configuration
 */
import config from "@/config";

const getMcpConfigurationJson = (
    apiKey: string,
    organizationId: string,
    stekId: string,
    organizationName: string,
    stekName: string
): string => {
    const serverName = `mystekz-${organizationName}-${stekName}`
        .toLowerCase()
        .replace(/\s+/g, "-");
    const mcpConfig = {
        mcpServers: {
            [serverName]: {
                command: "docker",
                args: [
                    "run",
                    "--rm",
                    "-i",
                    `registry.gitlab.com/stekz/mystekz/mystekz/mcp-server:${config.mcpServer.imageVersion}`,
                    "--api-key",
                    apiKey,
                    "--stek-id",
                    stekId,
                    "--organization-id",
                    organizationId,
                ],
                disabled: false,
                alwaysAllow: [],
                timeout: 300,
            },
        },
    };

    return JSON.stringify(mcpConfig, null, 2);
};

const dialogRef = inject<Ref<DynamicDialogInstance> | undefined>("dialogRef");
const dialogData = dialogRef!.value.data as DialogData;

const apiKeyData = ref<ApiKeyCreated | undefined>(dialogData.apiKey);
const mcpData = ref<McpData | undefined>(dialogData.mcpData);
const isMcp = computed(() => Boolean(dialogData.isMcp));
const mcpApiKey = ref<ApiKeyCreated | undefined>(dialogData.mcpApiKey);

const apiKeyCopied = ref(false);
const mcpConfigCopied = ref(false);
const cliCommandCopied = ref(false);
const mcpConfigJson = ref("");
const claudeCliCommand = ref("");

onMounted(() => {
    if (isMcp.value && mcpData.value?.organization && mcpData.value?.stek) {
        generateMcpConfiguration();
    }
});

/**
 * Generate Claude CLI command for MCP
 *
 * @param apiKey - The API key to use for authentication
 * @param organizationId - The organization ID to use in the configuration
 * @param stekId - The stek ID to use in the configuration
 * @param organizationName - The organization name to use in the server name
 * @param stekName - The stek name to use in the server name
 * @returns A string containing the Claude CLI command
 */
const getClaudeCliCommand = (
    apiKey: string,
    organizationId: string,
    stekId: string,
    organizationName: string,
    stekName: string
): string => {
    const serverName = `mystekz-${organizationName}-${stekName}`
        .toLowerCase()
        .replace(/\s+/g, "-");
    const commandConfig = {
        command: "docker",
        args: [
            "run",
            "--rm",
            "-i",
            `registry.gitlab.com/stekz/mystekz/mystekz/mcp-server:${config.mcpServer.imageVersion}`,
            "--api-key",
            apiKey,
            "--stek-id",
            stekId,
            "--organization-id",
            organizationId,
        ],
        disabled: false,
        alwaysAllow: [],
        timeout: 300,
    };

    // Escape single quotes in the JSON string for the bash command
    const escapedJson = JSON.stringify(commandConfig).replace(/'/g, "'\\''");

    return `claude mcp add-json ${serverName} '${escapedJson}'`;
};

const generateMcpConfiguration = () => {
    if (
        !mcpData.value?.organization ||
        !mcpData.value?.stek ||
        !mcpApiKey.value
    ) {
        return;
    }

    try {
        // Generate MCP config JSON
        mcpConfigJson.value = getMcpConfigurationJson(
            mcpApiKey.value.key,
            mcpData.value.organization.identity,
            mcpData.value.stek.identity,
            mcpData.value.organization.name,
            mcpData.value.stek.name
        );

        // Generate Claude CLI command
        claudeCliCommand.value = getClaudeCliCommand(
            mcpApiKey.value.key,
            mcpData.value.organization.identity,
            mcpData.value.stek.identity,
            mcpData.value.organization.name,
            mcpData.value.stek.name
        );
    } catch (error) {
        console.error("Error generating MCP config:", error);
        mcpConfigJson.value = JSON.stringify(
            {
                error: "Failed to generate configuration",
                details: String(error),
            },
            null,
            2
        );
        claudeCliCommand.value = "Error generating CLI command";
    }
};

const copyApiKey = (key: string) => {
    navigator.clipboard.writeText(key);
    apiKeyCopied.value = true;
    window.setTimeout(() => {
        apiKeyCopied.value = false;
    }, 2000);
};

const copyMcpConfig = (config: string) => {
    navigator.clipboard.writeText(config);
    mcpConfigCopied.value = true;
    window.setTimeout(() => {
        mcpConfigCopied.value = false;
    }, 2000);
};

const copyCliCommand = (command: string) => {
    navigator.clipboard.writeText(command);
    cliCommandCopied.value = true;
    window.setTimeout(() => {
        cliCommandCopied.value = false;
    }, 2000);
};

const formatDate = (dateStr?: Date | string | null): string => {
    if (!dateStr) return "Never";
    if (typeof dateStr === "string") {
        return new Date(dateStr).toLocaleString();
    } else {
        return dateStr.toLocaleString();
    }
};
</script>

<style scoped lang="scss">
.api-key-created-content {
    padding: 0.5rem 0 1rem;
}

.warning-banner {
    font-weight: 600;
    padding: 1rem;
    border-radius: 4px;
    display: flex;
    align-items: center;
    background-color: #fff3e0;
    color: #e65100;
    margin-bottom: 1rem;

    i {
        margin-right: 0.5rem;
    }
}

.api-key-display,
.config-display {
    background-color: #f5f5f5;
    padding: 1rem;
    border-radius: 4px;
    font-family: monospace;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border: 1px solid #e0e0e0;
    margin: 1rem 0;
}

.api-key-value,
.config-value {
    word-break: break-all;
    font-size: 0.95rem;
    padding-right: 1rem;
    flex: 1;
    white-space: pre-wrap;
    overflow-x: auto;
}

.api-key-info {
    margin: 1rem 0;
}

.info-row {
    margin-bottom: 0.5rem;
    line-height: 1.5;
}

.info-label {
    font-weight: 600;
    color: #495057;
    margin-right: 0.5rem;
    display: inline-block;
    width: 100px;
}

.buttons {
    display: flex;
    justify-content: flex-end;
    margin-top: 1rem;
}

h4 {
    font-size: 1.1rem;
    margin-bottom: 0.75rem;
}

.mt-4 {
    margin-top: 1.5rem !important;
}

.mb-2 {
    margin-bottom: 0.5rem !important;
}
</style>
